import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ILogyxErrorAnswer,
  LogyxErrorId,
  readErrorStatus,
  readLogyxError,
} from 'services/api/errors';
import { OrderStatus } from 'types/Orders.types';

// What was refused: Finish order (POST /v1/carts/order) or "Create new cart"
// (POST /v1/carts/new). Logyx core names the order it refused for in
// `data.order_id` (logix-core docs/ordering.md, "Error ids"); Forms says why and
// opens that order (docs/BP-1219-ordering.md).
export type RefusedAction = 'finishOrder' | 'newCart';

export interface IOrderRefusal {
  orderId: number;
  level: 'info' | 'warning' | 'error';
  // An i18n key, and its values.
  text: string;
  values?: Record<string, string>;
}

// A0:029's toast, for Finish order and Retry: the order may already be at the
// supplier. Only an UNKNOWN one is being checked (Logyx core's outcome job). A
// FAILED one Logyx core will not send again ("Check with supplier") or a PENDING
// one, set by hand, waits for the supplier: nobody else checks it.
export const outcomeUnknownText = (answer: ILogyxErrorAnswer): string =>
  readErrorStatus(answer) === OrderStatus.UNKNOWN
    ? 'This order may already be at the supplier. It is being checked, so it cannot be sent again.'
    : "This order may already be at the supplier. It can't be ordered again until the supplier has checked it.";

// The toast for an answer that names an order, or undefined when there is none
// (or, for a new cart, an error id this version does not know: its message is
// then shown as any other error's).
export const readOrderRefusal = (
  answer: ILogyxErrorAnswer,
  action: RefusedAction
): IOrderRefusal | undefined => {
  const orderId = answer.data?.order_id;
  if (!orderId) {
    return undefined;
  }

  switch (answer.errorId) {
    case LogyxErrorId.CART_ORDER_IN_PROGRESS:
      return {
        orderId,
        level: 'info',
        text: 'This cart is already being ordered. Here is that order.',
      };
    case LogyxErrorId.ORDER_SENDING:
      return {
        orderId,
        level: 'info',
        text: 'This order is still being sent. Its status updates on this page.',
      };
    case LogyxErrorId.ORDER_OUTCOME_UNKNOWN:
      return {
        orderId,
        level: 'warning',
        text:
          action === 'newCart'
            ? "This cart's order may already be at the supplier, so no new cart was made. Here is that order."
            : outcomeUnknownText(answer),
      };
    case LogyxErrorId.CART_ORDER_FAILED:
      return {
        orderId,
        level: 'warning',
        text: 'This cart has an order that failed. Retry it, or change the cart and order it again.',
      };
  }

  if (action === 'newCart') {
    return undefined;
  }

  // Finish order: an order that was saved and did not go through (FAILED).
  const first = answer.errors[0];
  const reason =
    answer.data?.outcome_message ||
    (first && typeof first === 'object' ? first.message : undefined);
  return reason
    ? {
        orderId,
        level: 'error',
        text: 'The order could not be placed: {{reason}}',
        values: { reason },
      }
    : { orderId, level: 'error', text: 'The order could not be placed.' };
};

// Returns a handler for a failed request: true when the answer named an order,
// whose toast it showed and whose page it opened; false when the caller handles
// the error.
export const useOrderRefusal = (
  singleOrderPath: string,
  action: RefusedAction
) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (error: unknown): boolean => {
    const refusal = readOrderRefusal(readLogyxError(error), action);
    if (!refusal) {
      return false;
    }
    toast[refusal.level](
      refusal.values ? t(refusal.text, refusal.values) : t(refusal.text)
    );
    navigate(singleOrderPath.replace(':id', String(refusal.orderId)));
    return true;
  };
};
